import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export default function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "newest";
  const featured = params.get("featured") === "true";
  const bestseller = params.get("bestseller") === "true";
  const minPrice = params.get("min_price") || "";
  const maxPrice = params.get("max_price") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = { sort, limit: 50 };
    if (category) q.category = category;
    if (search) q.search = search;
    if (featured) q.featured = true;
    if (bestseller) q.bestseller = true;
    if (minPrice) q.min_price = minPrice;
    if (maxPrice) q.max_price = maxPrice;
    api.get("/products", { params: q })
      .then((r) => { setProducts(r.data.items); setTotal(r.data.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, search, sort, featured, bestseller, minPrice, maxPrice]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === null) next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const FiltersPanel = (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest brand-text-primary font-bold mb-2">Kategori</div>
        <div className="space-y-1">
          <button
            data-testid="filter-cat-all"
            onClick={() => updateParam("category", "")}
            className={`block w-full text-left text-sm px-3 py-2 rounded-xl ${!category ? "brand-soft-bg brand-text-primary font-bold" : "hover:bg-[#FAF9F6]"}`}
          >
            Semua Kategori
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-cat-${c.slug}`}
              onClick={() => updateParam("category", c.slug)}
              className={`block w-full text-left text-sm px-3 py-2 rounded-xl ${
                category === c.slug ? "brand-soft-bg brand-text-primary font-bold" : "hover:bg-[#FAF9F6]"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest brand-text-primary font-bold mb-2">Rentang Harga</div>
        <div className="grid grid-cols-2 gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={minPrice}
            onChange={(e) => updateParam("min_price", e.target.value)}
            className="h-10 rounded-xl"
            data-testid="filter-min-price"
          />
          <Input
            type="number"
            placeholder="Max"
            value={maxPrice}
            onChange={(e) => updateParam("max_price", e.target.value)}
            className="h-10 rounded-xl"
            data-testid="filter-max-price"
          />
        </div>
      </div>
      <Button
        variant="outline"
        onClick={() => setParams(new URLSearchParams())}
        className="w-full rounded-xl"
        data-testid="filter-reset-btn"
      >
        <X size={14} className="mr-1" /> Reset Filter
      </Button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest brand-text-primary font-bold">Katalog</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">
            {search ? `Hasil "${search}"` : category ? categories.find((c) => c.slug === category)?.name || "Produk" : "Semua Produk"}
          </h1>
          <div className="text-sm text-[#5C5C5C] mt-1">{total} produk ditemukan</div>
        </div>
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="md:hidden rounded-xl" data-testid="mobile-filter-btn">
                <SlidersHorizontal size={16} className="mr-1" /> Filter
              </Button>
            </SheetTrigger>
            <SheetContent side="left">
              <SheetHeader><SheetTitle>Filter Produk</SheetTitle></SheetHeader>
              <div className="mt-4">{FiltersPanel}</div>
            </SheetContent>
          </Sheet>
          <Select value={sort} onValueChange={(v) => updateParam("sort", v)}>
            <SelectTrigger className="w-[180px] rounded-xl" data-testid="sort-select">
              <SelectValue placeholder="Urutkan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Terbaru</SelectItem>
              <SelectItem value="bestseller">Terlaris</SelectItem>
              <SelectItem value="price_low">Harga Termurah</SelectItem>
              <SelectItem value="price_high">Harga Termahal</SelectItem>
              <SelectItem value="rating">Rating Tertinggi</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-6">
        <aside className="hidden md:block bg-white border border-[#E5E5E5] rounded-2xl p-5 h-fit sticky top-24">
          {FiltersPanel}
        </aside>
        <section>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={`skeleton-${i}`} className="aspect-[3/4] shimmer rounded-2xl" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="bg-white border border-[#E5E5E5] rounded-2xl p-12 text-center text-[#5C5C5C]">
              Tidak ada produk yang cocok.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
